"use client"

import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Check, X, RefreshCw, ExternalLink, Search, Zap, Key, Trash2 } from "@/lib/icons"
import { cn } from "@/lib/utils"
import { useDeviceStore } from "@/lib/device-store"
import { ButtplugDevice } from "@/lib/devices/buttplug-device"
import { deviceController } from "@/lib/device-controller"
import { useTranslations } from "next-intl"

interface QuickConnectDeviceModalProps {
  open: boolean
  onClose: () => void
}

const DEFAULT_SETTINGS = {
  minPosition: 0,
  maxPosition: 100,
  invertControls: false,
  strokeSpeedLimit: 10,
  smoothingEnabled: true,
  smoothingAmount: 30,
  controlMode: "2-axis" as const,
  yOnlySpeedSensitivity: 50,
  restPosition: 0,
}

// Lightweight connect flow for the script player's "No device" indicator.
// For full device management (settings, firmware, multiple devices) point users
// at the Device Manager tab in the editor — this modal only covers the two
// fastest paths to a working connection.
export function QuickConnectDeviceModal({ open, onClose }: QuickConnectDeviceModalProps) {
  const t = useTranslations("quickConnectDeviceModal")
  const { devices, activeDeviceId, addDevice, updateDevice, connectDevice, disconnectDevice, removeDevice } = useDeviceStore()

  const [connectingId, setConnectingId] = useState<string | null>(null)
  const [error, setError] = useState("")
  const [removeConfirmId, setRemoveConfirmId] = useState<string | null>(null)

  const [handyKey, setHandyKey] = useState(() => {
    try { return localStorage.getItem("handy_saved_key") ?? "" } catch { return "" }
  })
  const [isConnectingHandy, setIsConnectingHandy] = useState(false)

  const [bluetoothName, setBluetoothName] = useState("")
  const [isScanning, setIsScanning] = useState(false)
  const [scanResults, setScanResults] = useState<Array<{ name: string; index: number }>>([])
  const [isConnectingBluetooth, setIsConnectingBluetooth] = useState(false)

  const handleRemoveConfirmed = async (id: string) => {
    const device = devices.find((d) => d.id === id)
    if (device?.isConnected) {
      await disconnectDevice(id)
    }
    removeDevice(id)
    setRemoveConfirmId(null)
  }

  const handleConnectExisting = async (id: string) => {
    const device = devices.find((d) => d.id === id)

    setConnectingId(id)
    setError("")
    if (device?.isConnected) {
      await disconnectDevice(id)
    }
    const ok = await connectDevice(id)
    setConnectingId(null)
    if (ok) onClose()
    else setError(t("couldnTReconnectThatDeviceItsKeyOrPairin"))
  }

  const handleQuickConnectHandy = async () => {
    const key = handyKey.trim()
    if (!key) { setError(t("enterYourHandyConnectionKey")); return }
    setIsConnectingHandy(true)
    setError("")
    try {
      const existing = devices.find((d) => d.type === "handy" && d.connectionType === "wifi")
      const id = existing
        ? existing.id
        : addDevice({
            name: "The Handy",
            type: "handy",
            connectionType: "wifi",
            isConnected: false,
            latencyMs: 0,
            settings: DEFAULT_SETTINGS,
          })
      updateDevice(id, { connectionKey: key })
      try { localStorage.setItem("handy_saved_key", key) } catch {}

      const connected = await connectDevice(id)
      if (connected) onClose()
      else setError(t("connectionFailedCheckYourKeyAndTryAgain"))
    } finally {
      setIsConnectingHandy(false)
    }
  }

  const handleScanBluetooth = async () => {
    setIsScanning(true)
    setError("")
    try {
      const buttplug = new ButtplugDevice()
      const found = await buttplug.scanDevices()
      if (found.length === 0) {
        setError(buttplug.getLastConnectionError() || t("noDevicesFoundMakeSureIntifaceCentralIsR"))
      }
      setScanResults(found)
    } catch {
      setError(t("failedToScanMakeSureIntifaceCentralIsRun"))
    } finally {
      setIsScanning(false)
    }
  }

  const handleConnectBluetooth = async () => {
    const name = bluetoothName.trim()
    if (!name) { setError(t("selectOrEnterADeviceName")); return }
    setIsConnectingBluetooth(true)
    setError("")

    // This scan/connect flow goes through Intiface Central (ButtplugDevice),
    // NOT Handy's own cloud API -- it previously created a `type: "handy"`
    // device here, which routes to HandyDevice.connect() and treats
    // whatever key it's given as a Handy cloud Connect Key. Handing it a
    // scanned device NAME (e.g. "HandyXXXX") instead of a real Connect Key
    // meant this could never actually succeed -- Scan and Connect were
    // silently using two different, incompatible systems. `type: "buttplug"`
    // routes through the same class that found the device during scan.
    const existing = devices.find((d) => d.type === "buttplug")
    const id = existing
      ? existing.id
      : addDevice({
          name: name || "Intiface Device",
          type: "buttplug",
          connectionType: "server",
          isConnected: false,
          latencyMs: 0,
          settings: DEFAULT_SETTINGS,
        })
    updateDevice(id, { name, connectionKey: "ws://localhost:12345" })

    const connected = await connectDevice(id)
    setIsConnectingBluetooth(false)
    if (connected) {
      onClose()
    } else {
      setError(
        deviceController.getLastConnectionError("buttplug") ||
        t("failedToConnectMakeSureIntifaceCentralIs"),
      )
      updateDevice(id, { connectionKey: undefined })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="!z-[210] max-w-md" overlayClassName="!z-[205]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            {t("connectADevice")}
          </DialogTitle>
          <DialogDescription>{t("syncThisScriptToYourDeviceWhileYouPrevie")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {devices.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">{t("yourDevices")}</Label>
              <div className="space-y-2">
                {devices.map((d) => (
                  <div key={d.id} className="flex items-center justify-between rounded-lg border px-3 py-2">
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="truncate text-sm font-medium">{d.name}</p>
                        {d.isConnected && (
                          <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">
                            {t("connected")}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground capitalize">
                        {t("connectionTypeWithActive", { type: d.connectionType, active: activeDeviceId === d.id ? t("activeSuffix") : "" })}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Button
                        size="sm"
                        variant={d.isConnected ? "outline" : "default"}
                        onClick={() => handleConnectExisting(d.id)}
                        disabled={connectingId === d.id}
                      >
                        {connectingId === d.id
                          ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                          : d.isConnected ? t("reconnect") : t("connect")}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        title={t("removeDevice")}
                        onClick={() => setRemoveConfirmId(d.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2 rounded-lg border p-3">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <Key className="h-3.5 w-3.5" />{t("theHandyWiFi")}
            </Label>
            <Input
              type="password"
              placeholder={t("connectionKey")}
              value={handyKey}
              onChange={(e) => { setHandyKey(e.target.value); setError("") }}
              className="font-mono"
              disabled={isConnectingHandy}
            />
            <Button className="w-full" onClick={handleQuickConnectHandy} disabled={isConnectingHandy || !handyKey.trim()}>
              {isConnectingHandy
                ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />{t("connecting")}</>
                : <><Check className="h-4 w-4 mr-2" />{t("connect")}</>}
            </Button>
            <a
              href="https://www.handyfeeling.com/setup"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" />{t("getYourKeyAtHandyfeelingComSetup")}
            </a>
          </div>

          <div className="space-y-2 rounded-lg border p-3">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5" />{t("intifaceCentralHandyOrCompatible")}
            </Label>
            <Button variant="outline" className="w-full" onClick={handleScanBluetooth} disabled={isScanning || isConnectingBluetooth}>
              {isScanning
                ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />{t("scanning")}</>
                : <><Search className="h-4 w-4 mr-2" />{t("scanForDevices")}</>}
            </Button>
            {scanResults.length > 0 && (
              <div className="space-y-1 max-h-28 overflow-y-auto">
                {scanResults.map((r) => (
                  <button
                    key={r.index}
                    type="button"
                    onClick={() => setBluetoothName(r.name)}
                    className={cn(
                      "w-full text-left text-sm px-2 py-1.5 rounded border transition-colors",
                      bluetoothName === r.name ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"
                    )}
                  >
                    {r.name}
                  </button>
                ))}
              </div>
            )}
            <Input
              placeholder={t("orEnterDeviceNameManually")}
              value={bluetoothName}
              onChange={(e) => { setBluetoothName(e.target.value); setError("") }}
              disabled={isConnectingBluetooth}
            />
            <Button variant="outline" className="w-full" onClick={handleConnectBluetooth} disabled={isConnectingBluetooth || !bluetoothName.trim()}>
              {isConnectingBluetooth
                ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />{t("connecting")}</>
                : t("connect")}
            </Button>
          </div>

          {error && (
            <p className="text-sm text-destructive flex items-center gap-1.5">
              <X className="h-3.5 w-3.5 shrink-0" />{error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("close")}</Button>
        </DialogFooter>
      </DialogContent>

      {/* A plain Dialog, not AlertDialog — AlertDialogContent hardcodes z-50 with
          no overlayClassName escape hatch, and this modal itself already needs
          !z-[210]/!z-[205] to render above whatever it's opened on top of (e.g.
          the booru detail modal). Stacking a default-z-50 AlertDialog here would
          render it BEHIND the device modal, making it unusable. */}
      <Dialog open={!!removeConfirmId} onOpenChange={(v) => { if (!v) setRemoveConfirmId(null) }}>
        <DialogContent className="!z-[220] max-w-sm" overlayClassName="!z-[215]">
          <DialogHeader>
            <DialogTitle>{t("removeThisDevice")}</DialogTitle>
            <DialogDescription>
              {(() => {
                const device = devices.find((d) => d.id === removeConfirmId)
                const label = device ? `"${device.name}"` : t("thisDevice")
                return `${label} will be forgotten and disconnected. You'll need to reconnect it (key/pairing) to use it again.`
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoveConfirmId(null)}>{t("cancel")}</Button>
            <Button
              variant="destructive"
              onClick={() => { if (removeConfirmId) handleRemoveConfirmed(removeConfirmId) }}
            >
              {t("remove")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}
